import { AlertTriangle } from "lucide-react";
import { SERVICE_LABELS, useServiceDown, type ServiceKey } from "@/hooks/useServiceHealth";

interface CurtainDownProps {
  service: ServiceKey;
  /** Name of the feature as the visitor knows it, e.g. "Quick Discovery". */
  feature?: string;
  className?: string;
}

/**
 * Theatre-style "temporarily closed" notice shown in place of a feature whose
 * underlying service is offline. Returns null while everything is healthy.
 */
export const CurtainDown = ({ service, feature, className = "" }: CurtainDownProps) => {
  const { isDown } = useServiceDown(service);
  if (!isDown) return null;

  return (
    <div
      role="status"
      className={`relative overflow-hidden rounded-xl border border-primary/25 bg-card/80 p-6 text-center shadow-lg ${className}`}
    >
      {/* curtain folds */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-30"
        style={{
          background:
            "repeating-linear-gradient(90deg, hsl(var(--primary) / 0.35) 0px, hsl(var(--primary) / 0.1) 18px, hsl(var(--primary) / 0.35) 36px)",
        }}
      />
      <div className="relative z-10 space-y-2">
        <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full border border-primary/40 bg-background/70">
          <AlertTriangle className="h-5 w-5 text-primary" />
        </div>
        <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground">
          Curtain down
        </p>
        <h3 className="text-lg font-semibold text-foreground">
          {feature ?? SERVICE_LABELS[service]} is closed for a moment
        </h3>
        <p className="mx-auto max-w-md text-sm text-muted-foreground">
          We hit a problem with {SERVICE_LABELS[service].toLowerCase()}. The crew has already been
          notified and this reopens by itself the moment it's fixed — everything else stays open.
        </p>
      </div>
    </div>
  );
};

/** Small inline pill for tight spots (buttons rows, cards). */
export const CurtainDownPill = ({ service, feature }: CurtainDownProps) => {
  const { isDown } = useServiceDown(service);
  if (!isDown) return null;
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-card/70 px-3 py-1 text-xs text-muted-foreground">
      <AlertTriangle className="h-3.5 w-3.5 text-primary" />
      {feature ?? SERVICE_LABELS[service]} temporarily unavailable
    </span>
  );
};

export default CurtainDown;
